import { useState } from 'react';
import { MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePublicTransferQuestions } from '../hooks';

/**
 * The recipient's Q&A thread.
 *
 * ---------------------------------------------------------------------------
 * YOU ONLY EVER SEE YOUR OWN QUESTIONS — AND THE UI SHOULD SAY SO
 *
 * The server scopes this read to the visitor's SESSION, never to the link. An
 * open link is one token handed to many people by design, and in a dataroom the
 * questions themselves disclose what an investor is worried about.
 *
 * The note under the composer is not decoration: someone about to ask "are you
 * also talking to Acquirer B?" needs to know whether the other investors
 * holding this link can read it. They cannot.
 * ---------------------------------------------------------------------------
 *
 * Bodies render as TEXT. React escapes by default and there is deliberately no
 * markdown or HTML rendering here — the content is written by anonymous third
 * parties, and answers are read by the sender in their own dashboard.
 */
export function TransferQuestionsPanel({ token }: { token: string }) {
  const { questions, loading, ask, asking } = usePublicTransferQuestions(token);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setError(null);
    try {
      await ask(body);
      setDraft('');
    } catch {
      setError('Your question could not be sent. Please try again.');
    }
  };

  return (
    <section className="rounded-lg border p-4">
      <div className="mb-3 flex items-center gap-2">
        <MessageCircle className="h-4 w-4" />
        <h2 className="font-medium">Questions</h2>
      </div>

      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : questions.length > 0 ? (
        <ul className="mb-4 space-y-3">
          {questions.map((question) => (
            <li key={question.questionId} className="rounded-md bg-muted/50 p-3">
              <p className="whitespace-pre-wrap text-sm">{question.body}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {new Date(question.createdAt).toLocaleString()}
              </p>
              {question.answerBody ? (
                <div className="mt-3 border-l-2 border-primary/40 pl-3">
                  <p className="whitespace-pre-wrap text-sm">{question.answerBody}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Answered{' '}
                    {question.answeredAt ? new Date(question.answeredAt).toLocaleString() : ''}
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-xs italic text-muted-foreground">Awaiting a reply</p>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Ask the sender a question…"
        maxLength={4000}
        rows={3}
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Only you and the sender can see your questions.
        </p>
        <Button size="sm" onClick={() => void submit()} disabled={!draft.trim() || asking}>
          {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </section>
  );
}
